/**
 * How /channels words and prints what it shows (spec §3.3–§3.5, §4): the
 * channel names, tile labels and values, short dates, campaign states, the
 * type / family / format labels and the platform notes. Channel names are
 * proper nouns from the one file of platform names (src/utils/platformLabel)
 * and are never translated. Every other word is a literal t('…') key, so the
 * locale integrity test sees it. null is "—", never 0. Pure.
 *
 * @module services/channels/channelFormat
 */
import type { TFunction } from 'i18next';
import { creativeFormatLabel, type CreativeFormat } from '@/services/creatives/creativeFormat';
import { DASH, fmtCount, fmtMoney, fmtPct, objectiveText } from '@/services/overview/overviewFormat';
import { platformShortLabel } from '@/utils/platformLabel';
import { shortDate } from '@/utils/shortDate';
import {
  CHANNEL_PLATFORMS, isAdChannel,
  type CampaignState, type ChannelEmpty, type ChannelPlatform, type NoSpendEmpty, type PlatformTile, type TargetingFamily,
  type TileFormat, type TileMetric,
} from './channelTypes';

export type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The tab names: proper nouns, never translated (Global Constraints › i18n).
 * They come from the one file of platform names (R33). A name literal here
 * would be a second source, and platformLabel.test.ts fails on one. The keys of
 * fromEntries are plain strings. CHANNEL_PLATFORMS lists every ChannelPlatform,
 * so the record is total, and the test pins all five.
 */
export const CHANNEL_LABEL: Readonly<Record<ChannelPlatform, string>> = Object.fromEntries(
  CHANNEL_PLATFORMS.map((p) => [p, platformShortLabel(p)] as const),
) as Record<ChannelPlatform, string>;

/**
 * The name the empty card (ChannelEmptyState) speaks with on D1 and D4: the one ticked channel's, as D2's tabs name it.
 * None ticked (every channel) or several, and an id that is no ad channel, are null: the page's channel chip names them.
 */
export const soleChannelLabel = (channels: readonly string[]): string | null =>
  (channels.length === 1 && isAdChannel(channels[0]) ? CHANNEL_LABEL[channels[0]] : null);

/**
 * E's headline, D2's card and D1's and D4's alike (rules-04 G3): "<channel> ยังไม่ได้เชื่อมต่อ", or the no-spend
 * words, which name the channel when there is one. Literal keys, so the locale integrity test sees them.
 */
export const emptyHeadline = (kind: ChannelEmpty['kind'], channel: string | null, t: TFn): string => {
  if (kind === 'notConnected') return t('channels.empty.notConnected', { channel });
  return channel === null ? t('channels.empty.noSpendAny') : t('channels.empty.noSpend', { channel });
};

/**
 * The card's headline as the file and the chat say it (rule 04.3, D1 and D4): the one ticked channel is named, none or
 * several are not. No date and no 28-day hint: those are the page's own advice, and a channel not connected has
 * neither (rules-04 G3).
 */
export const emptySentence = (empty: NoSpendEmpty, t: TFn): string => emptyHeadline(empty.kind, soleChannelLabel(empty.channels), t);

/** The card D2 draws for D1's and D4's E: their period spent nothing, so there is no last-run date to give. */
export const cardEmptyOf = (empty: NoSpendEmpty): ChannelEmpty =>
  (empty.kind === 'notConnected' ? { kind: 'notConnected' } : { kind: 'noSpend', lastSpendDate: null });

/** Exhaustive over TileMetric; literal keys, so the locale integrity test sees them. */
const TILE_LABEL: Readonly<Record<TileMetric, (t: TFunction) => string>> = {
  reactions: (t) => t('channels.tile.reactions'),
  comments: (t) => t('channels.tile.comments'),
  shares: (t) => t('channels.tile.shares'),
  linkClicks: (t) => t('channels.tile.linkClicks'),
  leads: (t) => t('channels.tile.leads'),
  cpl: (t) => t('channels.tile.cpl'),
  videoViews: (t) => t('channels.tile.videoViews'),
  viewRate: (t) => t('channels.tile.viewRate'),
  cpv: (t) => t('channels.tile.cpv'),
  conversions: (t) => t('channels.tile.conversions'),
  conversionValue: (t) => t('channels.tile.conversionValue'),
  roas: (t) => t('channels.tile.roas'),
  cpa: (t) => t('channels.tile.cpa'),
  engagements: (t) => t('channels.tile.engagements'),
  reach: (t) => t('channels.tile.reach'),
  frequency: (t) => t('channels.tile.frequency'),
  videoCompletions: (t) => t('channels.tile.videoCompletions'),
  sessions: (t) => t('channels.tile.sessions'),
  engagedSessions: (t) => t('channels.tile.engagedSessions'),
  webConversions: (t) => t('channels.tile.webConversions'),
  paidShare: (t) => t('channels.tile.paidShare'),
};

export const tileLabel = (metric: TileMetric, t: TFunction): string => TILE_LABEL[metric](t);

/**
 * A tile's name on the page, in the file and in the chat. CPA, CPL and ROAS name the objective they read: "CPL ·
 * Conversion" under "all" (rules-04 E4), "CPA · Awareness" with Awareness selected (rules-04 H1).
 */
export const tileTitle = (tile: Pick<PlatformTile, 'metric' | 'objective'>, t: TFunction): string =>
  (tile.objective === null ? tileLabel(tile.metric, t) : `${tileLabel(tile.metric, t)} · ${objectiveText(tile.objective, t)}`);

/**
 * The line under a tile that reads "—" because the objective it names has no current-period row in the page's scope,
 * in any currency (rules-04 H2): "ไม่มีแคมเปญ Conversion ในช่วงนี้". null for every other tile, and for every other "—".
 */
export const tileReason = (tile: PlatformTile, t: TFn): string | null =>
  (tile.noObjectiveRows && tile.objective !== null
    ? t('channels.noObjectiveCampaigns', { objective: objectiveText(tile.objective, t) })
    : null);

/** ROAS as "3.25x"; null is "—". */
export const fmtRoas = (n: number | null): string => (n === null ? DASH : `${n.toFixed(2)}x`);

const fmtValue = (format: TileFormat, n: number | null, currency: string | null): string => {
  if (format === 'money') return fmtMoney(n, currency);
  if (format === 'money4') return fmtMoney(n, currency, 4);
  if (format === 'pct') return fmtPct(n);
  if (format === 'roas') return fmtRoas(n);
  return fmtCount(n);
};

/** A tile as box 3 prints it. The state wins over any value ("ไม่ได้รายงาน" is D1's word, rules-04 D4); a counted null (a ratio over 0) is "—". */
export const fmtTile = (tile: PlatformTile, currency: string | null, t: TFn): string => {
  if (tile.state === 'notReported') return t('overview.notReported');
  if (tile.state === 'notShown') return t('channels.notShownV1');
  if (tile.state === 'none') return DASH;
  return fmtValue(tile.format, tile.value, currency);
};

/**
 * R27: the app's one short date (from the common.month* keys), so D2 dates
 * read as the trend axis does. A wrapper, so the channel modules have one
 * import.
 *
 * R36: every D2 date also carries its Gregorian year: "12 ต.ค. 2026" /
 * "12 Oct 2026". Facebook data spans 36 months, so a bare "จบ 12 ต.ค." for a
 * 2024 campaign would read as this month. The year is the ISO date's own, so
 * the output never depends on today.
 */
export const fmtShortDate = (iso: string, t: TFn): string => `${shortDate(iso, t)} ${iso.slice(0, 4)}`;

/** R26: Active / หยุด / จบ / จบ 12 ต.ค. 2026 / —. */
export const campaignStateText = (s: CampaignState, t: TFn): string => {
  if (s.kind === 'active') return t('channels.state.active');
  if (s.kind === 'paused') return t('channels.state.paused');
  if (s.kind === 'unknown') return t('channels.state.unknown');
  if (s.endDate === null) return t('channels.state.ended');
  return t('channels.state.endedOn', { date: fmtShortDate(s.endDate, t) });
};

/**
 * The matched BUY_TYPES entry as written; null = "ไม่ระบุประเภท", its own word: "Unclassified" is the naming rule's
 * (a name outside the convention, rules-04 E1).
 */
export const typeLabel = (type: string | null, t: TFn): string =>
  (type === null ? t('channels.filters.noType') : type);

/** CORE / LAL / BROAD / RTG as written; UNCLASSIFIED = Unclassified. */
export const familyLabel = (f: TargetingFamily, t: TFn): string =>
  (f === 'UNCLASSIFIED' ? t('looker.coverage.unclassified') : f);

/** The shared format's word (D3 §5: Static / Video / Carousel / อื่น ๆ); null = "ไม่ระบุ format", D2's own words. */
export const formatLabel = (format: CreativeFormat | null, t: TFn): string =>
  (format === null ? t('channels.breakdown.noFormat') : creativeFormatLabel(format, t));

/** The notes under box 3 (spec §3.3) and under the GA4 tiles (§3.5), in order. */
export const platformNotes = (platform: ChannelPlatform, t: TFunction): string[] => {
  if (platform === 'tiktok') return [t('channels.notes.engagementsTiktok')];
  if (platform === 'line') {
    return [
      t('channels.notes.lineReachFrequency'),
      t('channels.notes.lineCompletions'),
      t('channels.notes.lineLinkClicks'),
      t('channels.notes.engagementsLine'),
    ];
  }
  if (platform === 'analytics') return [t('channels.notes.ga4AllTraffic'), t('channels.notes.ga4NoActiveUsers')];
  return [];
};
